import { APP_CONTEXTS, NOT_SAVED_ID } from "app/model"
import { isCreateAppContext, isDspDealAppContext, isForecastAppContext } from "app/tools/app-context"
import { deepCopy } from "app/tools/common"
import { getDefaultAuctionTypes } from "auction/model"
import { CAMPAIGN_LANGUAGES, CAMPAIGN_STATUSES, DEFAULT_CAMPAIGN_LANGUAGE, ICampaign } from "campaign/model"
import { DEFAULT_RADIUS_VALUE } from "coordinate/model"
import { IFormInventory, INVENTORY_MAP_VIEW_TYPES } from "form/inventory/model"
import { v4 as uuidv4 } from "uuid"
import {
  DEFAULT_DSP_DEAL_AUCTION_TYPE,
  DEFAULT_FORM_CURRENCY,
  FORM_TABS,
  GEO_TARGETS_FORM_TABS,
  GEO_TARGETS_TYPES,
  IAppForm,
  IForm,
  IFormData,
  IFormState,
  IGeoTargetsFileImport,
  IGeoTargetsForm,
  SELECT_FILTER_VALUES,
  SUMMARY_TABS
} from "../model"

export function getDefaultFormState(): IFormState {
  return {
    campaignCreateForm: getDefaultAppFormState(APP_CONTEXTS.CAMPAIGN_CREATE),
    campaignEditForm: getDefaultAppFormState(APP_CONTEXTS.CAMPAIGN_EDIT),
    forecastCreateForm: getDefaultAppFormState(APP_CONTEXTS.FORECAST_CREATE),
    forecastEditForm: getDefaultAppFormState(APP_CONTEXTS.FORECAST_EDIT),
    dspDealCreateForm: getDefaultAppFormState(APP_CONTEXTS.DSP_DEAL_CREATE),
    dspDealEditForm: getDefaultAppFormState(APP_CONTEXTS.DSP_DEAL_EDIT),
    isEnvironmentTreeView: true
  }
}

export function getDefaultAppFormState(context: APP_CONTEXTS, isLoading?: boolean): IAppForm {
  return {
    isLoading: isLoading ? true : false,
    error: "",
    form: getNewCampaignForm(context),
    inventory: getNewInventoryState(context),
    geoTargetsForm: getNewGeoTargetsForm(context)
  }
}

export function getNewCampaignForm(contextType: APP_CONTEXTS, campaign?: ICampaign): IForm {
  const isDspDeal = isDspDealAppContext(contextType)
  return {
    campaignId: campaign ? campaign.id : NOT_SAVED_ID,
    activeTab: FORM_TABS.CONFIGURATION,
    activeSummaryTab: SUMMARY_TABS.PARAMETERS,
    tempCampaignId: NOT_SAVED_ID,
    tempDspDealId: NOT_SAVED_ID,
    statusId: campaign ? campaign.status_id : CAMPAIGN_STATUSES.TEMPORARY,
    changes: {
      configuration: [],
      geoTargets: [],
      screens: [],
      audience: [],
      creative: []
    },
    context: {
      originalCampaign: campaign || null,
      appContext: contextType,
      isCreation: isCreateAppContext(contextType),
      isForecast: isForecastAppContext(contextType),
      isDspDeal,
      isPaused: campaign ? campaign.is_paused || false : false,
      refreshFormikForm: false,
      relations: {
        auctions: null,
        schedule: null,
        weathers: null,
        caps: null,
        coordinates: [],
        cities: [],
        postalCodes: [],
        states: [],
        countries: [],
        metropolitans: [],
        environments: null,
        ssps: null,
        audiences: [],
        operators: [],
        campaignSspDeals: [],
        userTags: [],
        dspDealAdvertiserDomains: [],
        dspDealFormats: [],
        dspDealSeats: [],
        // errors
        auctionsError: false,
        scheduleError: false,
        weathersError: false,
        capsError: false,
        environmentsError: false,
        sspsError: false,
        sspDealsError: false,
        operatorsError: false,
        audiencesError: false,
        userTagsError: false,
        dspDealFormatsError: false
      },
      isSavingCampaign: false,
      isSavingTempCampaign: false,
      saveError: null,
      initialData: null,
      lastSavedData: null,
      configuration: {
        isValid: false,
        errors: {}
      },
      screens: {
        isValid: true,
        errors: {}
      },
      geoTargets: {
        isValid: true,
        errors: {}
      },
      creatives: {
        isValid: true,
        errors: {}
      },
      environment: {
        initialForecast: null,
        currentForecast: {}
      },
      operator: {
        initialForecast: null,
        currentForecast: {}
      },
      mediaCreative: {
        initialForecast: null,
        currentForecast: {}
      }
    },
    configuration: {
      main: {
        name: campaign ? campaign.product || "" : "",
        description: campaign ? campaign.description : null,
        advertiser_id: !isDspDeal && campaign ? campaign.advertiser_id : null,
        language_id:
          campaign && campaign.language_id ? (campaign.language_id as CAMPAIGN_LANGUAGES) : DEFAULT_CAMPAIGN_LANGUAGE,
        max_views: campaign ? campaign.max_views || 0 : 0,
        order_id: campaign ? campaign.order_id : null,
        unlimitedImpressions: campaign ? campaign.max_views === 0 : true,
        startDate: campaign && campaign.start_date ? new Date(campaign.start_date) : null,
        endDate: campaign && campaign.end_date ? new Date(campaign.end_date) : null,
        userTagIds: [],
        isBiddingOnlyOnDeals:
          campaign && typeof campaign.is_bidding_only_on_deals === "boolean" ? campaign.is_bidding_only_on_deals : false
      },
      budget: {
        budget: campaign ? campaign.budget : null,
        max_daily_spend: campaign ? campaign.max_daily_spend : null,
        max_bid: campaign && typeof campaign.rate === "number" ? campaign.rate : null,
        auctions: getDefaultAuctionTypes(isDspDeal),
        hasAuctionError: false,
        equalizeDailyMaxSpendFromSchedule:
          campaign && typeof campaign.equalizing_filter === "boolean" ? campaign.equalizing_filter : true,
        currency_id: (campaign ? campaign.currency_id || DEFAULT_FORM_CURRENCY : DEFAULT_FORM_CURRENCY) as any
      },
      schedule: {
        min_celsius: campaign ? campaign.min_celsius : null,
        max_celsius: campaign ? campaign.max_celsius : null,
        scheduleItems: [],
        noSchedule: true,
        weathers: []
      },
      caps: {
        month: null,
        week: null,
        day: null,
        hour: null,
        minute: null
      },
      dspDeal: {
        id: campaign && campaign.dspDeal ? campaign.dspDeal.id : null,
        auction_type: campaign && campaign.dspDeal ? campaign.dspDeal.auction_type : DEFAULT_DSP_DEAL_AUCTION_TYPE,
        bid_floor: campaign && campaign.dspDeal ? campaign.dspDeal.bid_floor || 0 : 0,
        dealid: campaign && campaign.dspDeal ? campaign.dspDeal.dealid : uuidv4(),
        description: campaign && campaign.dspDeal ? campaign.dspDeal.description : null,
        dsp_id: campaign && campaign.dspDeal ? campaign.dspDeal.dsp_id : null,
        advertiserDomainIds: [],
        formatIds: [],
        seatIds: [],
        isSynchronizingFormat: false
      }
    },
    geoTargets: {
      coordinates: [],
      cities: [],
      postalCodes: [],
      states: [],
      countries: [],
      metropolitans: [],
      fileImport: getDefaultGeoTargetsFileImportState()
    },
    screens: {
      environmentsIds: [],
      environmentFilter: "",
      environmentSelectFilter: SELECT_FILTER_VALUES.ALL,
      environmentExpandedIds: [],
      isSynchronizingEnvironments: false,
      hasEnvironmentsSynchronizeError: false,
      isLoadingEnvironmentsForecast: false,
      sspIds: [],
      isLoadingSsp: true,
      isSynchronizingSsp: false,
      hasSspSynchronizeError: false,
      allSspDeals: [],
      campaignSspDeals: [],
      isLoadingAllSspDeals: true,
      hasLoadingAllSspDealsError: false,
      isSynchronizingSspDeal: false,
      hasSspDealSynchronizeError: false,
      allOperators: [],
      isLoadingAllOperators: false,
      hasLoadingAllOperatorsError: false,
      operators: [],
      isSynchronizingOperator: false,
      isLoadingOperatorsForecast: false,
      hasOperatorSynchronizeError: false,
      operatorFilter: "",
      operatorSelectFilter: SELECT_FILTER_VALUES.ALL
    },
    audience: {
      selectedIds: [],
      selectFilter: SELECT_FILTER_VALUES.ALL,
      filter: "",
      isSynchronizing: false,
      hasSynchronizeError: false
    },
    creatives: {
      medias: [],
      filter: SELECT_FILTER_VALUES.ALL,
      isLoadingForecast: false,
      hasLoadingForecastError: false,
      selectedRequiredMediaCreativeIds: [],
      isSynchronizing: false,
      hasSynchronizeError: false
    }
  }
}

export function getDefaultGeoTargetsFileImportState(type?: GEO_TARGETS_TYPES): IGeoTargetsFileImport {
  return {
    isPopupOpened: false,
    isParsing: false,
    parsingError: "",
    rows: null,
    isSaving: false,
    files: null,
    error: "",
    importType: "replace",
    geoTargetsType: type || GEO_TARGETS_TYPES.COORDINATE
  }
}

export function getNewInventoryState(contextType: APP_CONTEXTS, campaign?: ICampaign): IFormInventory {
  return {
    isCreation: isCreateAppContext(contextType),
    isForecast: isForecastAppContext(contextType),
    tempCampaignId: NOT_SAVED_ID,
    campaignName: campaign ? campaign.product || "" : "",
    contextType,
    lastUpdate: new Date(),
    isRefreshing: false,
    hasRefreshError: false,
    mapViewType: INVENTORY_MAP_VIEW_TYPES.SCREENS,
    totalGeoTargets: 0,
    totalScreens: 0,
    totalImpressions: 0,
    totalSpend: 0,
    cpmForecastTotalImpressions: 0,
    cpmForecastTotalScreens: 0,
    cpmForecastTotalSpend: 0,
    cpmForecastGroups: [],
    screens: [],
    isMapVisible: false,
    isMapLoading: false,
    isMapLoaded: false,
    isMapRefreshing: false,
    mapImpressionsQuantiles: [],
    mapMaxImpressionsValue: 0
  }
}

export function getNewGeoTargetsForm(contextType: APP_CONTEXTS): IGeoTargetsForm {
  return {
    context: contextType,
    isForecast: isForecastAppContext(contextType),
    isCreation: true,
    name: "",
    latitude: null,
    longitude: null,
    radius: DEFAULT_RADIUS_VALUE,
    activeTab: GEO_TARGETS_FORM_TABS.SHAPE,
    toUpdateEntityId: NOT_SAVED_ID,
    toAddShape: null,
    isSelectingShape: false,
    isDeletingSelection: false,
    noIntersectionError: false,
    selection: {
      cityIds: [],
      coordinateIds: [],
      postalCodeIds: [],
      stateIds: [],
      countryIds: [],
      metropolitanIds: []
    }
  }
}

export function getFormData(form: IForm): IFormData {
  return {
    configuration: deepCopy(form.configuration),
    geoTargets: deepCopy(form.geoTargets),
    screens: deepCopy(form.screens),
    audience: deepCopy(form.audience),
    creatives: deepCopy(form.creatives)
  }
}
